import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiClient } from '../api/client';
import { useApi } from './provider';
import { projectKeys } from './projects';
import { taskKeys } from './tasks';

/**
 * Slice 11 — GitHub App integration hooks.
 *
 * Cache key conventions: org-scoped state (the installation, repo
 * list) lives under `['github', 'org', ...]`; per-task branch
 * mutations invalidate the existing `taskKeys.detail` family so the
 * task page picks up the new `github_branch_name` automatically.
 */

export const githubKeys = {
  installation: ['github', 'org', 'installation'] as const,
  installUrl: ['github', 'org', 'install-url'] as const,
  repositories: (page: number, perPage: number) =>
    ['github', 'org', 'repositories', page, perPage] as const,
  taskPullRequests: (workspaceSlug: string, taskUlid: string) =>
    ['github', 'task', 'pull-requests', workspaceSlug, taskUlid] as const,
};

// ── Installation ─────────────────────────────────────────────────────────

export interface GithubInstallationAttrs {
  organization_id: string;
  github_installation_id: number;
  account_login: string;
  account_type: 'User' | 'Organization';
  installed_by_user_id: string | null;
  installed_at: string | null;
  suspended_at: string | null;
}

interface GithubInstallationPayload {
  data?: { id: string; attributes?: GithubInstallationAttrs };
}

/**
 * Returns the org's current GitHub App installation, or `null` if
 * none. Maps the API's 404 ("not installed") to a successful
 * `data === null` so the SPA can branch cleanly without try/catch.
 */
export const githubInstallationQueryOptions = (api: ApiClient) =>
  queryOptions({
    queryKey: githubKeys.installation,
    // Installation status changes rarely once set; cache aggressively
    // so the WorkspaceSettings GitHub card renders instantly on revisit.
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error, response } = await api.GET(
        '/v1/organizations/current/github/installation',
      );
      if (response.status === 404) return null;
      if (error) throw error;
      return data ?? null;
    },
    retry: (failureCount, error) => {
      const status = (error as { status?: number } | null)?.status;
      // 403 (not org owner) and 404 (no installation) are terminal.
      if (status === 403 || status === 404) return false;
      return failureCount < 2;
    },
  });

export const useGithubInstallation = () => {
  const api = useApi();
  return useQuery(githubInstallationQueryOptions(api));
};

// ── Install URL ──────────────────────────────────────────────────────────

interface GithubInstallUrlPayload {
  data?: { url: string; state: string; expires_at: string };
}

/**
 * Mutation rather than query — the state token expires after 10 min,
 * so the SPA mints a fresh URL each time the user clicks "Install on
 * GitHub" rather than caching one.
 */
export const useGetGithubInstallUrl = () => {
  const api = useApi();
  return useMutation({
    mutationFn: async () => {
      const { data, error } = await api.GET(
        '/v1/organizations/current/github/install-url',
      );
      if (error) throw error;
      return data as GithubInstallUrlPayload;
    },
  });
};

// ── Complete install ────────────────────────────────────────────────────

export interface CompleteGithubInstallInput {
  /**
   * Optional. GitHub usually returns this on the Setup URL redirect,
   * but some App configurations cause it to be omitted — in that
   * case the API discovers the installation via the App installations
   * endpoint, scoped by the state token's issuance window.
   */
  installation_id?: number;
  state: string;
}

export const useCompleteGithubInstall = () => {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CompleteGithubInstallInput) => {
      const { data, error } = await api.POST(
        '/v1/organizations/current/github/install-callback',
        { body: input },
      );
      if (error) throw error;
      return data;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: githubKeys.installation });
    },
  });
};

// ── Disconnect ───────────────────────────────────────────────────────────

export const useDeleteGithubInstallation = () => {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { error } = await api.DELETE(
        '/v1/organizations/current/github/installation',
      );
      if (error) throw error;
    },
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: githubKeys.installation }),
        // Branch / repo lists implicitly become empty too.
        qc.invalidateQueries({ queryKey: ['github', 'org'] }),
      ]);
    },
  });
};

// ── Repositories ─────────────────────────────────────────────────────────

export interface GithubRepoListItem {
  owner: string;
  name: string;
  full_name: string;
}

interface GithubRepoListPayload {
  data?: GithubRepoListItem[];
  meta?: { total_count?: number; page?: number; per_page?: number };
}

/**
 * Per-page repo list. Used by the project settings repo picker; the
 * picker pages through as the user scrolls / searches. Cached
 * separately per (page, perPage) to avoid re-fetching pages already
 * seen.
 */
export const useGithubRepositories = (page = 1, perPage = 30) => {
  const api = useApi();
  return useQuery({
    queryKey: githubKeys.repositories(page, perPage),
    queryFn: async () => {
      const { data, error } = await api.GET(
        '/v1/organizations/current/github/repositories',
        { params: { query: { page, per_page: perPage } as never } },
      );
      if (error) throw error;
      return data as GithubRepoListPayload;
    },
  });
};

// ── Task branch ──────────────────────────────────────────────────────────

export interface CreateTaskBranchInput {
  branch_type: 'task' | 'feature' | 'fix' | 'chore';
  base_branch?: string;
}

/**
 * Create a branch on the task's project's linked repo. On success,
 * invalidates the per-task detail cache so the task page picks up
 * the new `github_branch_name` field immediately. We also bust the
 * project task list (the "Branch: feature/FLUX-42-..." chip can
 * appear on cards if we choose to surface it there).
 */
export const useCreateTaskBranch = (workspaceSlug: string, taskUlid: string) => {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateTaskBranchInput) => {
      const { data, error } = await api.POST(
        '/v1/workspaces/{workspace_slug}/tasks/{taskUlid}/github/branch',
        {
          params: { path: { workspace_slug: workspaceSlug, taskUlid } },
          body: input,
        },
      );
      if (error) throw error;
      return data;
    },
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: taskKeys.detail(workspaceSlug, taskUlid) }),
        qc.invalidateQueries({ queryKey: ['tasks', 'project', workspaceSlug] }),
      ]);
    },
  });
};

export const useDeleteTaskBranch = (workspaceSlug: string, taskUlid: string) => {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data, error } = await api.DELETE(
        '/v1/workspaces/{workspace_slug}/tasks/{taskUlid}/github/branch',
        { params: { path: { workspace_slug: workspaceSlug, taskUlid } } },
      );
      if (error) throw error;
      return data;
    },
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: taskKeys.detail(workspaceSlug, taskUlid) }),
        qc.invalidateQueries({ queryKey: ['tasks', 'project', workspaceSlug] }),
      ]);
    },
  });
};

// ── Task pull requests (slice 12) ────────────────────────────────────────

/**
 * Per-task pull-request list. Read-only — entirely populated by the
 * webhook receiver. The "Pull requests" card on TaskDetailPage reads
 * this; mutations to the list happen via GitHub events, not user
 * actions.
 */
export interface TaskPullRequestAttrs {
  task_id: string;
  github_repo_owner: string;
  github_repo_name: string;
  pr_number: number;
  pr_state: 'open' | 'draft' | 'closed' | 'merged';
  pr_title: string;
  pr_url: string;
  pr_author_login: string | null;
  linked_at: string | null;
  updated_at: string | null;
}

interface TaskPullRequestsPayload {
  data?: Array<{ id: string; attributes?: TaskPullRequestAttrs }>;
}

export const taskPullRequestsQueryOptions = (
  api: ApiClient,
  workspaceSlug: string,
  taskUlid: string,
) =>
  queryOptions({
    queryKey: githubKeys.taskPullRequests(workspaceSlug, taskUlid),
    // Polling is overkill — the webhook receiver writes server-side.
    // We invalidate this on task detail mounts (cache hit usually
    // covers the read) and rely on natural staleTime expiry for
    // long-mounted views to pick up new PRs.
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await api.GET(
        '/v1/workspaces/{workspace_slug}/tasks/{taskUlid}/pull-requests',
        {
          params: { path: { workspace_slug: workspaceSlug, taskUlid } },
        },
      );
      if (error) throw error;
      return data as TaskPullRequestsPayload;
    },
  });

export const useTaskPullRequests = (
  workspaceSlug: string | undefined,
  taskUlid: string | undefined,
) => {
  const api = useApi();
  return useQuery({
    ...taskPullRequestsQueryOptions(api, workspaceSlug ?? '', taskUlid ?? ''),
    enabled: Boolean(workspaceSlug && taskUlid),
  });
};

// Re-export `projectKeys` so consumers extending this file can find
// related cache keys without a separate import — keeps the GitHub
// hooks self-contained while still composing into the wider key
// hierarchy.
export { projectKeys };
